/**
 * Public Adjuster API Service (PR3, 2026-11-04)
 *
 * TypeScript client wrapping the public-adjuster REST endpoints.
 * Uses the existing `api` utility from utils/api.ts for auth + error handling.
 */

import { api } from '../utils/api';

const BASE = '/api/v1/public-adjuster';

// ---------------------------------------------------------------------------
// TYPES
// ---------------------------------------------------------------------------

export interface PACase {
  case_id: string;
  client_name: string;
  business_name: string;
  event_type: string;
  counsel_agent: boolean;
  granted_at?: string;
}

export interface PACaseListResponse {
  cases: PACase[];
}

export interface PACaseFiguresResponse {
  case_id: string;
  figures: Record<string, unknown>;
}

export interface PABriefResponse {
  case_id: string;
  brief: Record<string, unknown>;
  markdown: string;
  counsel_agent: boolean;
}

export interface PADocument {
  id: string;
  filename: string;
  doc_type: string | null;
  status: string | null;
  created_at: string | null;
  size_bytes: number | null;
  provenance: string | null;
}

export interface PADocumentsResponse {
  case_id: string;
  documents: PADocument[];
  count: number;
}

export interface PAUploadResponse {
  case_id: string;
  upload_id: string;
  filename: string;
  provenance: string;
  status: string;
}

export interface PAPositionItem {
  type: string;
  present: boolean;
  meta?: Record<string, unknown>;
}

export interface PACarrierInbound {
  id: string;
  kind: string;
  amount: number | null;
  status: string | null;
  source: string;
  created_at: string | null;
  lines: unknown[] | null;
}

export interface PAPositionsResponse {
  case_id: string;
  policyholder: PAPositionItem[];
  carrier: PACarrierInbound[];
  counsel_agent: boolean;
}

export interface PANoteBody {
  text: string;
  subject?: string;
}

export interface PANoteResponse {
  case_id: string;
  note_id: string;
  created_at: string;
}

export interface PAReadyBody {
  note?: string;
}

export interface PAReadyResponse {
  case_id: string;
  ready_for_review_at: string;
}

// ---------------------------------------------------------------------------
// API FUNCTIONS
// ---------------------------------------------------------------------------

/** List all cases where this public-adjuster org has an active grant. */
export async function listCases(): Promise<PACaseListResponse> {
  return api.get<PACaseListResponse>(`${BASE}/cases`);
}

/** Read-only binding figures for a granted case. */
export async function getFigures(caseId: string): Promise<PACaseFiguresResponse> {
  return api.get<PACaseFiguresResponse>(`${BASE}/cases/${caseId}/figures`);
}

/** War Room brief for a granted case, filtered by the PA's privilege tier. */
export async function getBrief(caseId: string): Promise<PABriefResponse> {
  return api.get<PABriefResponse>(`${BASE}/cases/${caseId}/brief`, { showErrorToast: false });
}

/** List client-visible documents for a granted case. */
export async function getDocuments(caseId: string): Promise<PADocumentsResponse> {
  return api.get<PADocumentsResponse>(`${BASE}/cases/${caseId}/documents`);
}

/** Upload a document to a granted case. */
export async function uploadDocument(
  caseId: string,
  file: File,
  docType?: string,
): Promise<PAUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  if (docType) {
    formData.append('doc_type', docType);
  }
  return api.upload<PAUploadResponse>(`${BASE}/cases/${caseId}/documents`, formData);
}

/** Policyholder demand/POL refs + carrier-submitted positions. */
export async function getPositions(caseId: string): Promise<PAPositionsResponse> {
  return api.get<PAPositionsResponse>(`${BASE}/cases/${caseId}/positions`);
}

/** Append a position note to a granted case. */
export async function postNote(caseId: string, body: PANoteBody): Promise<PANoteResponse> {
  return api.post<PANoteResponse>(`${BASE}/cases/${caseId}/notes`, body);
}

/** Mark the PA grant ready for attorney review. */
export async function markReadyForReview(
  caseId: string,
  body?: PAReadyBody,
): Promise<PAReadyResponse> {
  return api.post<PAReadyResponse>(`${BASE}/cases/${caseId}/ready-for-review`, body ?? {});
}
